import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Plus, Edit, Trash2, Search, Globe, ChefHat, FileJson, FileSpreadsheet, Link as LinkIcon, LogOut, BarChart3, ClipboardList, Wrench, Users, Sparkles, Wand2, HeartPulse, TrendingUp, ImageIcon, ArrowUpAZ, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';

const API_URL = process.env.REACT_APP_BACKEND_URL || import.meta?.env?.VITE_BACKEND_URL || '';

const isAIRecipe = (recipe) => {
    const sources = [
        recipe.source,
        recipe.source_type,
        recipe.collection_method,
        recipe.created_by,
    ].filter(Boolean).map(value => String(value).toLowerCase());

    return sources.some(value =>
        value.includes('ai') || value.includes('demand_generation')
    ) || recipe._ai_generated === true || recipe.generated_by_ai === true;
};

export const AdminRecipesPage = () => {
    const [recipes, setRecipes] = useState([]);
    const [filteredRecipes, setFilteredRecipes] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [statusFilter, setStatusFilter] = useState('all');
    const [sortOrder, setSortOrder] = useState('name-asc');
    const [photoFilter, setPhotoFilter] = useState('all');
    const [sourceFilter, setSourceFilter] = useState('all');
    const [counts, setCounts] = useState({ all: 0, published: 0, unpublished: 0, draft: 0 });
    const navigate = useNavigate();

    const getAuthHeaders = () => {
        const token = localStorage.getItem('admin_token');
        return { Authorization: `Bearer ${token}` };
    };

    const fetchRecipes = async (filter = 'all') => {
        try {
            const url = filter === 'all' 
                ? `${API_URL}/api/admin/recipes`
                : `${API_URL}/api/admin/recipes?status_filter=${filter}`;
            const response = await axios.get(url, { headers: getAuthHeaders() });
            setRecipes(response.data.recipes);
            setFilteredRecipes(response.data.recipes);
            setCounts(response.data.counts || { all: 0, published: 0, unpublished: 0, draft: 0 });
        } catch (error) {
            if (error.response?.status === 401) {
                toast.error('Session expired. Please login again.');
                navigate('/admin/login');
            } else {
                toast.error('Failed to fetch recipes');
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/admin/stats`, { headers: getAuthHeaders() });
            setStats(response.data);
        } catch (error) {
            console.error('Failed to fetch stats:', error);
        }
    };

    useEffect(() => {
        fetchRecipes(statusFilter);
        fetchStats();
    }, [statusFilter]);

    useEffect(() => {
        let filtered = [...recipes];

        if (searchQuery) {
            filtered = filtered.filter(r =>
                r.recipe_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                r.origin_country?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                r.slug?.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }

        if (photoFilter === 'with-photo') {
            filtered = filtered.filter(recipe => Boolean(recipe.image_url));
        } else if (photoFilter === 'missing-photo') {
            filtered = filtered.filter(recipe => !recipe.image_url);
        }

        if (sourceFilter === 'ai') {
            filtered = filtered.filter(isAIRecipe);
        } else if (sourceFilter === 'non-ai') {
            filtered = filtered.filter(recipe => !isAIRecipe(recipe));
        }

        filtered.sort((a, b) => {
            if (sortOrder === 'name-desc') {
                return (b.recipe_name || '').localeCompare(a.recipe_name || '', undefined, { sensitivity: 'base' });
            }
            if (sortOrder === 'country') {
                return (a.origin_country || '').localeCompare(b.origin_country || '', undefined, { sensitivity: 'base' })
                    || (a.recipe_name || '').localeCompare(b.recipe_name || '', undefined, { sensitivity: 'base' });
            }
            if (sortOrder === 'newest') {
                return new Date(b.updated_at || b.date_fetched || b.created_at || 0)
                    - new Date(a.updated_at || a.date_fetched || a.created_at || 0);
            }
            return (a.recipe_name || '').localeCompare(b.recipe_name || '', undefined, { sensitivity: 'base' });
        });

        setFilteredRecipes(filtered);
    }, [searchQuery, recipes, sortOrder, photoFilter, sourceFilter]);

    const handleDelete = async (slug) => {
        if (!window.confirm(`Are you sure you want to delete "${slug}"?`)) return;

        try {
            await axios.delete(`${API_URL}/api/admin/recipes/${slug}`, { headers: getAuthHeaders() });
            toast.success('Recipe deleted successfully');
            fetchRecipes();
        } catch (error) {
            toast.error('Failed to delete recipe');
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-700"></div>
            </div>
        );
    }

    return (
        <>
        <Helmet>
            <meta name="robots" content="noindex, nofollow" />
            <title>Recipe Management - Admin | Sous Chef Linguine</title>
        </Helmet>
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <header className="bg-white shadow-sm border-b">
                <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <ChefHat className="w-8 h-8 text-amber-700" />
                        <h1 className="text-xl font-bold text-gray-800">Admin Panel</h1>
                    </div>
                    <div className="flex items-center gap-3">
                        <Link to="/admin/review">
                            <Button variant="outline" size="sm" className="border-orange-300 text-orange-700 hover:bg-orange-50">
                                <ClipboardList className="w-4 h-4 mr-2" /> Review Queue
                            </Button>
                        </Link>
                        <Link to="/admin/repair">
                            <Button variant="outline" size="sm" className="border-amber-300 text-amber-700 hover:bg-amber-50">
                                <Wrench className="w-4 h-4 mr-2" /> Repair Tool
                            </Button>
                        </Link>
                        <Link to="/admin/users">
                            <Button variant="outline" size="sm" className="border-blue-300 text-blue-700 hover:bg-blue-50">
                                <Users className="w-4 h-4 mr-2" /> Users
                            </Button>
                        </Link>
                        <Link to="/admin/ai-review">
                            <Button variant="outline" size="sm" className="border-violet-300 text-violet-700 hover:bg-violet-50" data-testid="ai-review-nav-btn">
                                <Sparkles className="w-4 h-4 mr-2" /> AI Review
                            </Button>
                        </Link>
                        <Link to="/admin/ai-settings">
                            <Button variant="outline" size="sm" className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="ai-settings-nav-btn">
                                <Wand2 className="w-4 h-4 mr-2" /> AI Settings
                            </Button>
                        </Link>
                        <Link to="/admin/analytics">
                            <Button variant="outline" size="sm" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="analytics-nav-btn">
                                <BarChart3 className="w-4 h-4 mr-2" /> Analytics
                            </Button>
                        </Link>
                        <Link to="/admin/language-integrity">
                            <Button variant="outline" size="sm" className="border-red-300 text-red-700 hover:bg-red-50" data-testid="language-integrity-nav-btn">
                                <Globe className="w-4 h-4 mr-2" /> Language Integrity
                            </Button>
                        </Link>
                        <Link to="/admin/recipe-health">
                            <Button variant="outline" size="sm" className="border-orange-300 text-orange-700 hover:bg-orange-50" data-testid="recipe-health-nav-btn">
                                <HeartPulse className="w-4 h-4 mr-2" /> Recipe Health
                            </Button>
                        </Link>
                        <Link to="/admin/demand">
                            <Button variant="outline" size="sm" className="border-amber-300 text-amber-800 hover:bg-amber-50" data-testid="demand-nav-btn">
                                <TrendingUp className="w-4 h-4 mr-2" /> Demand & Drafts
                            </Button>
                        </Link>
                        <Link to="/admin/techniques">
                            <Button variant="outline" size="sm" className="border-purple-300 text-purple-700 hover:bg-purple-50">
                                <Wrench className="w-4 h-4 mr-2" /> Techniques
                            </Button>
                        </Link>
                        <Link to="/admin/techniques/import">
                            <Button variant="outline" size="sm" className="border-green-300 text-green-700 hover:bg-green-50">
                                <FileJson className="w-4 h-4 mr-2" /> Import Techniques
                            </Button>
                        </Link>
                        <Link to="/">
                            <Button variant="outline" size="sm">
                                <Globe className="w-4 h-4 mr-2" /> View Site
                            </Button>
                        </Link>
                        <Button variant="outline" size="sm" onClick={handleLogout}>
                            <LogOut className="w-4 h-4 mr-2" /> Logout
                        </Button>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-4 py-6">
                {/* Stats Cards */}
                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
                        <Card className={`cursor-pointer transition-all ${statusFilter === 'all' ? 'ring-2 ring-amber-500' : ''}`} onClick={() => setStatusFilter('all')}>
                            <CardContent className="p-4">
                                <div className="text-2xl font-bold text-amber-700">{counts.all}</div>
                                <div className="text-sm text-gray-500">Total Recipes</div>
                            </CardContent>
                        </Card>
                        <Card className={`cursor-pointer transition-all ${statusFilter === 'published' ? 'ring-2 ring-green-500' : ''}`} onClick={() => setStatusFilter('published')}>
                            <CardContent className="p-4">
                                <div className="text-2xl font-bold text-green-600">{counts.published}</div>
                                <div className="text-sm text-gray-500">âœ… Published</div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="p-4">
                                <div className="text-2xl font-bold text-emerald-600">{stats.visibility?.truly_visible || stats.public_site_shows || 0}</div>
                                <div className="text-sm text-gray-500">ðŸ‘ï¸ Truly Visible</div>
                                <div className="text-xs text-gray-400">On public site</div>
                            </CardContent>
                        </Card>
                        <Card className={`cursor-pointer transition-all ${statusFilter === 'unpublished' ? 'ring-2 ring-red-500' : ''}`} onClick={() => setStatusFilter('unpublished')}>
                            <CardContent className="p-4">
                                <div className="text-2xl font-bold text-red-600">{counts.unpublished}</div>
                                <div className="text-sm text-gray-500">âŒ Unpublished</div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="p-4">
                                <div className="text-2xl font-bold text-orange-600">{stats.visibility?.gap || 0}</div>
                                <div className="text-sm text-gray-500">âš ï¸ Gap</div>
                                <div className="text-xs text-gray-400">Published but hidden</div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="p-4">
                                <div className="text-2xl font-bold text-blue-600">{stats.recipes_by_country?.length || 0}</div>
                                <div className="text-sm text-gray-500">ðŸŒ Countries</div>
                            </CardContent>
                        </Card>
                    </div>
                )}
                
                {/* Visibility Issues Alert */}
                {stats?.visibility?.gap > 0 && (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4 text-sm">
                        <span className="font-medium text-orange-700">âš ï¸ Visibility Gap:</span>{' '}
                        <span className="text-orange-600">
                            {stats.visibility.gap} recipes are "published" but NOT visible on public site.
                        </span>
                        <span className="text-gray-600 ml-2">
                            (Missing: {stats.visibility.missing_continent > 0 && `continent=${stats.visibility.missing_continent}`}
                            {stats.visibility.missing_country > 0 && ` country=${stats.visibility.missing_country}`}
                            {stats.visibility.missing_required_fields > 0 && ` required_fields=${stats.visibility.missing_required_fields}`})
                        </span>
                        <a href="/admin/review" className="ml-2 text-blue-600 underline">View in Review Queue â†’</a>
                    </div>
                )}
                
                {/* Database Info Banner */}
                {stats?.db_info && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6 text-sm">
                        <span className="font-medium">ðŸ“Š Database:</span> {stats.db_info.database} | 
                        <span className="font-medium ml-2">Collection:</span> recipes | 
                        <span className="text-blue-600 ml-2">{stats.filter_explanation}</span>
                    </div>
                )}
                
                {/* Current Filter Indicator */}
                <div className="mb-4 flex items-center gap-2">
                    <span className="text-sm text-gray-500">Showing:</span>
                    <Badge variant={statusFilter === 'all' ? 'default' : 'outline'}>
                        {statusFilter === 'all' ? 'All Recipes' : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}
                    </Badge>
                    <span className="text-sm text-gray-500">({filteredRecipes.length} recipes)</span>
                    {statusFilter !== 'all' && (
                        <Button variant="ghost" size="sm" onClick={() => setStatusFilter('all')}>
                            Clear filter
                        </Button>
                    )}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3 mb-6">
                    <Link to="/admin/recipes/new">
                        <Button className="bg-amber-700 hover:bg-amber-800">
                            <Plus className="w-4 h-4 mr-2" /> New Recipe
                        </Button>
                    </Link>
                    <Link to="/admin/import-json">
                        <Button variant="outline">
                            <FileJson className="w-4 h-4 mr-2" /> Import JSON
                        </Button>
                    </Link>
                    <Link to="/admin/import-csv">
                        <Button variant="outline">
                            <FileSpreadsheet className="w-4 h-4 mr-2" /> Import CSV
                        </Button>
                    </Link>
                    <Link to="/admin/import-document">
                        <Button variant="outline">
                            <FileSpreadsheet className="w-4 h-4 mr-2" /> Import PDF/ODF
                        </Button>
                    </Link>
                    <Link to="/admin/import-scrape">
                        <Button variant="outline">
                            <LinkIcon className="w-4 h-4 mr-2" /> Scrape URL
                        </Button>
                    </Link>
                </div>

                {/* Search, sort, and review filters */}
                <div className="bg-white border rounded-lg p-4 mb-6 shadow-sm">
                    <div className="grid gap-3 lg:grid-cols-[minmax(260px,1fr)_190px_180px_180px]">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <Input
                                placeholder="Search by recipe name, country, or slug..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-10"
                                aria-label="Search recipes"
                            />
                        </div>
                        <label className="relative">
                            <ArrowUpAZ className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                            <select
                                value={sortOrder}
                                onChange={(e) => setSortOrder(e.target.value)}
                                className="h-10 w-full rounded-md border border-input bg-background pl-10 pr-3 text-sm"
                                aria-label="Sort recipes"
                            >
                                <option value="name-asc">Name Aâ€“Z</option>
                                <option value="name-desc">Name Zâ€“A</option>
                                <option value="country">Country, then name</option>
                                <option value="newest">Recently updated</option>
                            </select>
                        </label>
                        <label className="relative">
                            <ImageIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                            <select
                                value={photoFilter}
                                onChange={(e) => setPhotoFilter(e.target.value)}
                                className="h-10 w-full rounded-md border border-input bg-background pl-10 pr-3 text-sm"
                                aria-label="Filter by photo"
                            >
                                <option value="all">All photos</option>
                                <option value="with-photo">With photo</option>
                                <option value="missing-photo">Missing photo</option>
                            </select>
                        </label>
                        <label className="relative">
                            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                            <select
                                value={sourceFilter}
                                onChange={(e) => setSourceFilter(e.target.value)}
                                className="h-10 w-full rounded-md border border-input bg-background pl-10 pr-3 text-sm"
                                aria-label="Filter by recipe source"
                            >
                                <option value="all">All sources</option>
                                <option value="ai">AI created</option>
                                <option value="non-ai">Not marked AI</option>
                            </select>
                        </label>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-sm text-gray-500">
                        <span>
                            Showing <strong className="text-gray-800">{filteredRecipes.length}</strong> of {recipes.length} recipes
                        </span>
                        {(searchQuery || sortOrder !== 'name-asc' || photoFilter !== 'all' || sourceFilter !== 'all') && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                    setSearchQuery('');
                                    setSortOrder('name-asc');
                                    setPhotoFilter('all');
                                    setSourceFilter('all');
                                }}
                            >
                                Reset search and filters
                            </Button>
                        )}
                    </div>
                </div>

                {/* Recipes Table */}
                <Card>
                    <CardHeader>
                        <CardTitle>Recipes ({filteredRecipes.length})</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b">
                                        <th className="text-left py-3 px-2 w-28">Photo</th>
                                        <th className="text-left py-3 px-2">Recipe Name</th>
                                        <th className="text-left py-3 px-2">Country</th>
                                        <th className="text-left py-3 px-2">Continent</th>
                                        <th className="text-left py-3 px-2">Status</th>
                                        <th className="text-left py-3 px-2">Rating</th>
                                        <th className="text-right py-3 px-2">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredRecipes.map((recipe) => (
                                        <tr key={recipe.slug} className="border-b hover:bg-gray-50">
                                            <td className="py-3 px-2">
                                                {recipe.image_url ? (
                                                    <img
                                                        src={recipe.image_url}
                                                        alt={recipe.image_alt || recipe.recipe_name || 'Recipe'}
                                                        loading="lazy"
                                                        className="w-24 h-16 rounded-md border bg-gray-100 object-cover"
                                                    />
                                                ) : (
                                                    <div className="w-24 h-16 rounded-md border border-dashed bg-gray-50 flex flex-col items-center justify-center text-gray-400">
                                                        <ImageIcon className="w-5 h-5" />
                                                        <span className="text-[10px] mt-1">No photo</span>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="py-3 px-2">
                                                <div className="font-medium">{recipe.recipe_name || 'Untitled'}</div>
                                                <div className="text-xs text-gray-500">{recipe.slug}</div>
                                                {isAIRecipe(recipe) && (
                                                    <Badge variant="outline" className="mt-1 border-violet-300 text-violet-700">
                                                        <Sparkles className="w-3 h-3 mr-1" /> AI created
                                                    </Badge>
                                                )}
                                            </td>
                                            <td className="py-3 px-2">{recipe.origin_country || '-'}</td>
                                            <td className="py-3 px-2">{recipe.continent || '-'}</td>
                                            <td className="py-3 px-2">
                                                <Badge variant={recipe.status === 'published' ? 'default' : 'secondary'}>
                                                    {recipe.status || 'draft'}
                                                </Badge>
                                            </td>
                                            <td className="py-3 px-2">
                                                {recipe.average_rating > 0 ? `â­ ${recipe.average_rating}` : '-'}
                                            </td>
                                            <td className="py-3 px-2 text-right">
                                                <div className="flex justify-end gap-2">
                                                    <Link to={`/admin/recipes/${recipe.slug}/edit`}>
                                                        <Button size="sm" variant="outline">
                                                            <Edit className="w-4 h-4" />
                                                        </Button>
                                                    </Link>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        className="text-red-600 hover:text-red-700"
                                                        onClick={() => handleDelete(recipe.slug)}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {filteredRecipes.length === 0 && (
                                <div className="text-center py-8 text-gray-500">
                                    No recipes found. Start by adding a new recipe!
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
        </>
    );
};

export default AdminRecipesPage;

